import {
  Card,
  CardContent,
  Divider,
  DescriptionIcon,
  SendIcon,
  DownloadIcon,
  ClockIcon,
  FavoriteIcon,
  TrashIcon,
} from '@forge/common';
import type { FileStats, StorageUsage } from '@/api/files';

interface FileSidebarProps {
  readonly stats: FileStats;
  readonly storage: StorageUsage;
  readonly activeFilter: string;
  readonly onFilterChange: (filter: string) => void;
}

interface FolderItem {
  key: string;
  label: string;
  count: number;
  icon: JSX.Element;
}

const formatGb = (bytes: number): string => {
  const gb = bytes / 1024 ** 3;
  return gb >= 10 ? gb.toFixed(0) : gb.toFixed(1);
};

const FileSidebar = ({
  stats,
  storage,
  activeFilter,
  onFilterChange,
}: FileSidebarProps) => {
  const folders: FolderItem[] = [
    { key: 'all', label: 'All Files', count: stats.all, icon: <DescriptionIcon /> },
    { key: 'sent', label: 'Sent', count: stats.sent, icon: <SendIcon /> },
    { key: 'received', label: 'Received', count: stats.received, icon: <DownloadIcon /> },
    { key: 'pending', label: 'Pending', count: stats.pending, icon: <ClockIcon /> },
    { key: 'starred', label: 'Starred', count: stats.starred, icon: <FavoriteIcon /> },
    { key: 'deleted', label: 'Deleted', count: stats.deleted, icon: <TrashIcon /> },
  ];

  const totalStorageGB = formatGb(storage.quotaBytes);
  const usedStorageGB = formatGb(storage.usedBytes);
  const usagePercent =
    storage.quotaBytes > 0
      ? Math.min(
          100,
          Math.round((storage.usedBytes / storage.quotaBytes) * 100),
        )
      : 0;

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardContent>
          <p className="text-xs font-bold uppercase text-color-tertiary mb-2">Folders</p>
          <Divider />
          <div className="flex flex-col">
            {folders.map((folder) => (
              <button
                key={folder.key}
                onClick={() => onFilterChange(folder.key)}
                className={`flex items-center gap-2 px-2 py-2 rounded text-sm transition-colors w-full text-left ${
                  activeFilter === folder.key
                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                <span className="flex-shrink-0">{folder.icon}</span>
                <span className="flex-1">{folder.label}</span>
                <span className="text-xs text-color-tertiary tabular-nums">{folder.count}</span>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <p className="text-xs font-bold uppercase text-color-tertiary mb-2">Storage</p>
          <p className="text-sm mb-2">
            Used <span className="font-bold">{usedStorageGB} GB</span> of {totalStorageGB} GB
          </p>
          <div className="w-full h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
            <div
              className="h-full rounded-full bg-blue-500 transition-all"
              style={{ width: `${usagePercent}%` }}
            />
          </div>
          <p className="text-xs text-color-tertiary mt-1">{usagePercent}% used</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default FileSidebar;
